/**
 * Human-readable labels for case review profile + advice scope (mirrors API fields).
 */

export type AdviceScope = "single" | "joint" | "household";

export interface AdvisedParty {
  name: string;
  role?: string;
}

export interface CaseContextDisplayInput {
  clientName?: string | null;
  reviewProfile?: string | null;
  adviceScope?: AdviceScope | null;
  advisedParties?: AdvisedParty[];
  adviceScopeReasoning?: string | null;
  adviceType?: string | null;
  preAdviceRequired?: boolean;
}

const PRE_ADVICE_PROFILE_SLUGS = new Set([
  "phase-2-pre-advice",
  "pre-approval",
  "pre-approval-ex-crm",
  "documents-only",
]);

const ADVICE_SCOPE_LABELS: Record<AdviceScope, string> = {
  single: "Single client",
  joint: "Joint advice",
  household: "Household",
};

/** Fallback when GET /review-profiles is unavailable. */
export const FALLBACK_REVIEW_PROFILE_LABELS: Record<string, string> = {
  "phase-1-post-call": "Phase 1 — Post call",
  "phase-2-pre-advice": "Phase 2 — Pre advice",
  "phase-3-post-advice": "Phase 3 — Post advice",
  "documents-only": "Documents checklist",
  "pre-approval-ex-crm": "File review (documents)",
  "pre-approval": "File review (+ case record)",
};

export function formatAdviceScopeLabel(scope: AdviceScope | null | undefined): string | null {
  if (!scope) return null;
  return ADVICE_SCOPE_LABELS[scope] ?? scope;
}

export function reviewProfileLabel(
  slug: string | null | undefined,
  labelBySlug?: ReadonlyMap<string, string>,
): string | null {
  if (!slug?.trim()) return null;
  const key = slug.trim();
  return labelBySlug?.get(key) ?? FALLBACK_REVIEW_PROFILE_LABELS[key] ?? key.replace(/-/g, " ");
}

export function isPreAdviceReviewProfile(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return PRE_ADVICE_PROFILE_SLUGS.has(slug.trim());
}

/** Primary client line for headers (parties first, then case label). */
export function formatClientsHeading(input: CaseContextDisplayInput): string {
  const parties = (input.advisedParties ?? []).map((p) => p.name?.trim()).filter(Boolean) as string[];
  if (parties.length > 0) {
    return parties.join(" & ");
  }
  return (input.clientName ?? "").trim() || "Unnamed case";
}

export function formatClientsSubline(input: CaseContextDisplayInput): string | null {
  const scopeLabel = formatAdviceScopeLabel(input.adviceScope ?? undefined);
  const caseLabel = (input.clientName ?? "").trim();
  const parties = (input.advisedParties ?? []).map((p) => p.name?.trim()).filter(Boolean);
  const parts: string[] = [];
  if (scopeLabel) parts.push(scopeLabel);
  if (parties.length > 0 && caseLabel && !parties.every((n) => caseLabel.includes(n))) {
    parts.push(`Case label: ${caseLabel}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export interface CaseContextSummaryLine {
  term: string;
  detail: string;
}

/** Rows for the case context bar and footer summary. */
export function buildCaseContextSummary(
  input: CaseContextDisplayInput,
  labelBySlug?: ReadonlyMap<string, string>,
): CaseContextSummaryLine[] {
  const lines: CaseContextSummaryLine[] = [];

  const clients = formatClientsHeading(input);
  const scopeLabel = formatAdviceScopeLabel(input.adviceScope ?? undefined);
  const clientDetail = scopeLabel ? `${clients} (${scopeLabel.toLowerCase()})` : clients;
  lines.push({ term: "Clients", detail: clientDetail });

  const stage =
    reviewProfileLabel(input.reviewProfile, labelBySlug) ??
    (input.preAdviceRequired ? "Pre-advice" : input.preAdviceRequired === false ? "Post-advice" : null);
  if (stage) {
    lines.push({ term: "Review stage", detail: stage });
  }

  const advice = (input.adviceType ?? "").trim();
  if (advice) {
    lines.push({ term: "Advice type", detail: advice });
  }

  if (!input.adviceScope && !(input.advisedParties?.length)) {
    lines.push({
      term: "Advice scope",
      detail: "Not set yet — run case type detection after documents are classified",
    });
  }

  return lines;
}

/** Long case-type reasoning — show in a tooltip, not the context bar grid. */
export function getAdviceScopeReasoning(input: CaseContextDisplayInput): string | null {
  const text = (input.adviceScopeReasoning ?? "").trim();
  return text || null;
}
